import { useEffect, useState } from "react";
import axios from "axios";
import { Pause, Play, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";

const API = "/api";

const TIMEZONES = [
  "America/Lima", "America/Mexico_City", "America/Bogota", "America/Santiago",
  "America/Argentina/Buenos_Aires", "America/Sao_Paulo", "America/Guayaquil",
  "America/La_Paz", "America/Caracas", "America/Panama", "America/New_York",
  "America/Los_Angeles", "Europe/Madrid", "Europe/Lisbon", "Europe/Paris",
  "Asia/Kolkata",
];

/**
 * iter71 4.1 pausa manual + iter72 1.2 pausa programada (timezone del dueño).
 */
export const PauseNoticesConfig = ({ vehicleId }) => {
  const { t } = useI18n();
  const [paused, setPaused] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sched, setSched] = useState(null);
  const [schedSaving, setSchedSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/vehicles/${vehicleId}`)
      .then((r) => { if (alive) setPaused(!!r.data?.notices_paused); })
      .catch(() => { if (alive) setPaused(false); });
    axios.get(`${API}/vehicles/${vehicleId}/scheduled-pause`)
      .then((r) => { if (alive) setSched(r.data); })
      .catch(() => { if (alive) setSched({ enabled: false, start: "23:00", end: "06:00", timezone: "America/Lima" }); });
    return () => { alive = false; };
  }, [vehicleId]);

  const toggle = async () => {
    if (saving || paused === null) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/vehicles/${vehicleId}/pause-notices`, { paused: !paused });
      setPaused(!!r.data?.notices_paused);
      toast.success(r.data?.notices_paused
        ? "Avisos pausados. Quien escanee tu QR verá un mensaje neutro."
        : "Avisos reanudados. Tu QR vuelve a recibir avisos.");
    } catch (_e) {
      toast.error("No se pudo actualizar la pausa");
    } finally {
      setSaving(false);
    }
  };

  const saveSched = async (next) => {
    setSchedSaving(true);
    try {
      const payload = { enabled: next.enabled, start: next.start, end: next.end, timezone: next.timezone };
      const r = await axios.put(`${API}/vehicles/${vehicleId}/scheduled-pause`, payload);
      setSched({ ...payload, draft: false, active_now: r.data?.active_now });
      toast.success(next.enabled
        ? t("owner.pause.sched_saved", { start: next.start, end: next.end, tz: next.timezone })
        : t("owner.pause.sched_off"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("owner.pause.sched_error"));
    } finally {
      setSchedSaving(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-5 space-y-4 ${paused ? "border-amber-400/50 bg-amber-500/10" : "border-slate-200 bg-white"}`}
      data-testid="pause-notices-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-[#0B2545] flex items-center gap-2">
            {paused ? <Pause className="w-4 h-4 text-amber-400" /> : <Play className="w-4 h-4 text-[#1B84FF]" />}
            {paused ? t("owner.pause.title_paused") : t("owner.pause.title")}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {paused ? t("owner.pause.desc_paused") : t("owner.pause.desc")}
          </p>
        </div>
        <button
          onClick={toggle}
          disabled={saving || paused === null}
          className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 ${
            paused ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-amber-500/20 hover:bg-amber-500/30 text-amber-700 border border-amber-400/40"
          }`}
          data-testid="pause-notices-toggle-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : paused ? `▶️ ${t("owner.pause.resume_btn")}` : `⏸️ ${t("owner.pause.pause_btn")}`}
        </button>
      </div>

      {/* iter72 1.2 — pausa programada */}
      {sched && (
        <div className="border-t border-slate-200 pt-4" data-testid="scheduled-pause-section">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-xs font-bold text-[#0B2545] flex items-center gap-2">
                <Clock className="w-4 h-4 text-sky-400" />
                {t("owner.pause.sched_title")}
                {sched.enabled && sched.active_now && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 border border-amber-400/40" data-testid="scheduled-pause-active-badge">
                    {t("owner.pause.active_now")}
                  </span>
                )}
              </h4>
              <p className="text-[11px] text-slate-500 mt-1">
                {t("owner.pause.sched_hint")}
              </p>
            </div>
            <button
              onClick={() => {
                if (sched.enabled) {
                  saveSched({ ...sched, enabled: false }); // desactivar sí persiste (dirección segura)
                } else {
                  setSched({ ...sched, draft: true }); // solo abre el panel; persiste con "Guardar"
                }
              }}
              disabled={schedSaving}
              className={`shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-bold transition-colors disabled:opacity-50 ${
                sched.enabled ? "bg-sky-500 hover:bg-sky-400 text-black" : "bg-slate-100 hover:bg-white/20 text-slate-600"
              }`}
              data-testid="scheduled-pause-toggle"
            >
              {sched.enabled ? t("owner.pause.on") : sched.draft ? t("owner.pause.configuring") : t("owner.pause.off")}
            </button>
          </div>

          {(sched.enabled || sched.draft) && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
              <label className="text-[11px] text-slate-500">
                {t("owner.pause.from")}
                <input
                  type="time"
                  value={sched.start}
                  onChange={(e) => setSched({ ...sched, start: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-slate-100 border border-slate-200 text-[#0B2545] text-xs px-2 py-1.5"
                  data-testid="scheduled-pause-start"
                />
              </label>
              <label className="text-[11px] text-slate-500">
                {t("owner.pause.to")}
                <input
                  type="time"
                  value={sched.end}
                  onChange={(e) => setSched({ ...sched, end: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-slate-100 border border-slate-200 text-[#0B2545] text-xs px-2 py-1.5"
                  data-testid="scheduled-pause-end"
                />
              </label>
              <label className="text-[11px] text-slate-500 col-span-2 sm:col-span-1">
                {t("owner.pause.timezone")}
                <select
                  value={sched.timezone}
                  onChange={(e) => setSched({ ...sched, timezone: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-slate-100 border border-slate-200 text-[#0B2545] text-xs px-2 py-1.5"
                  data-testid="scheduled-pause-timezone"
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </label>
              <button
                onClick={() => saveSched({ enabled: true, start: sched.start, end: sched.end, timezone: sched.timezone })}
                disabled={schedSaving}
                className="px-3 py-2 rounded-xl text-[11px] font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors disabled:opacity-50"
                data-testid="scheduled-pause-save-btn"
              >
                {schedSaving ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : t("owner.pause.save")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PauseNoticesConfig;
